import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, Sparkles, ShieldCheck, ArrowRight, Gift } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (unix) => unix ? new Date(unix * 1000).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" }) : null;

export default function Account() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/subscription").then((r) => setData(r.data)).catch(() => setData({ plan: "free", type: "none" }));
  useEffect(() => { load(); refreshUser(); /* eslint-disable-next-line */ }, []);

  const cancel = async () => {
    setBusy(true);
    try { const { data: d } = await api.post("/subscription/cancel"); toast.success("Cancelled — you keep Premium until the period ends."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel."); } finally { setBusy(false); }
  };
  const resume = async () => {
    setBusy(true);
    try { await api.post("/subscription/resume"); toast.success("Welcome back — your plan will keep renewing."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not resume."); } finally { setBusy(false); }
  };

  const sub = data?.subscription;
  const type = data?.type;

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[560px] h-[560px] rounded-full opacity-25"
        style={{ background: "radial-gradient(circle, hsl(175 75% 50% / 0.5) 0%, transparent 62%)", filter: "blur(70px)" }} />
      <Navbar />
      <main className="relative max-w-xl mx-auto px-5 sm:px-8 pt-8 pb-24">
        <p className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Your account</p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mb-8">Plan &amp; billing</h1>

        <div className="glass rounded-2xl p-6 mb-6" data-testid="account-summary">
          <div className="text-sm text-muted-foreground">Signed in as</div>
          <div className="font-medium">{user?.email}</div>
        </div>

        {!data ? (
          <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : data.plan !== "premium" ? (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl border-2 border-border p-8 text-center" data-testid="account-free">
            <Sparkles className="w-10 h-10 text-primary mx-auto mb-4" />
            <h2 className="font-serif text-2xl mb-2">You're on the free plan</h2>
            <p className="text-muted-foreground text-sm mb-6">Upgrade to unlock Xcelio's real voice, the live avatar and unlimited practice.</p>
            <Button className="rounded-full bg-gradient-to-r from-blue-500 to-teal-400 text-white" onClick={() => navigate("/upgrade")} data-testid="account-upgrade-btn">
              See Premium <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </motion.div>
        ) : type === "paid" ? (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl border-2 border-primary/30 bg-card p-8" data-testid="account-paid">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck className="w-6 h-6 text-teal-500" />
              <span className="font-serif text-2xl">Xcelio Premium</span>
            </div>
            {sub ? (
              <>
                <div className="text-sm text-muted-foreground mb-1">
                  {sub.currency === "gbp" ? "£" : ""}{((sub.amount || 0) / 100).toFixed(2)} / {sub.interval} · status {sub.status}
                </div>
                {sub.cancel_at_period_end ? (
                  <div className="mt-4 rounded-xl bg-coral-50 border border-coral-200 p-4" data-testid="account-cancelling">
                    <p className="text-sm text-coral-700">
                      Cancelling. You keep Premium until <strong>{fmtDate(sub.current_period_end) || "the period ends"}</strong>, then move to free.
                    </p>
                    <Button className="rounded-full mt-4" onClick={resume} disabled={busy} data-testid="account-resume-btn">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Keep my Premium"}
                    </Button>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground mt-2 mb-6">Renews on <strong className="text-foreground">{fmtDate(sub.current_period_end) || "your next billing date"}</strong>.</p>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" className="rounded-full" data-testid="account-cancel-btn">Cancel plan</Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent data-testid="cancel-dialog">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Cancel your Premium?</AlertDialogTitle>
                          <AlertDialogDescription>
                            You'll keep full access until {fmtDate(sub.current_period_end) || "the end of your current period"}, then return to the free plan. You can resume any time before then.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="cancel-dialog-keep">Keep Premium</AlertDialogCancel>
                          <AlertDialogAction onClick={cancel} data-testid="cancel-dialog-confirm">Yes, cancel</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Your Premium is active. Billing details are momentarily unavailable — refresh in a moment.</p>
            )}
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl border-2 border-teal-400/40 bg-card p-8" data-testid="account-comp">
            <div className="flex items-center gap-2 mb-3">
              <Gift className="w-6 h-6 text-teal-500" />
              <span className="font-serif text-2xl">Premium — complimentary</span>
            </div>
            <p className="text-sm text-muted-foreground">You have full Premium access as a founder / invited member. There's nothing to pay and nothing to manage — enjoy everything Xcelio offers.</p>
            <Button className="rounded-full mt-6" onClick={() => navigate("/app")} data-testid="account-comp-home">Back to Xcelio</Button>
          </motion.div>
        )}
      </main>
    </div>
  );
}
